import { useCallback, useEffect, useState } from 'react';
import { Colors, Flex, IconChevronRight, Typography } from '@banhala/ui-kit';
import { bridge, isNil, isNotNil } from 'shared/src/utils';
import { useGoodsSno } from '../../../../../hooks/useGoodsSno';
import { useLogGoodsAnalyticsEvent } from '../../../../../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsPiecesFullLengthList } from '../queries/useGoodsPiecesFullLengthList';
import { useSizeInfoFromBridge } from '../queries/useSizeInfoFromBridge';
import { getSizeInfoText, withEunNeun, getInputNudgeText } from '../utils';
import { SizeVisualGuideImage } from './SizeVisualGuideImage';

interface SizeGuideFullLengthVisualContentProps {
  onHasData?: (hasData: boolean) => void;
}

export const SizeGuideFullLengthVisualContent = ({
  onHasData,
}: SizeGuideFullLengthVisualContentProps) => {
  const goodsSno = useGoodsSno();
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const { goodsPiecesFullLengthList } = useGoodsPiecesFullLengthList();

  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);

  const selectedSize = goodsPiecesFullLengthList[selectedSizeIndex];
  const selectedFullLength = selectedSize?.value ?? null;

  const { sizeInfo, setSizeInfo } = useSizeInfoFromBridge({
    fullLength: selectedFullLength,
  });

  const hasData =
    isNotNil(sizeInfo) &&
    isNotNil(sizeInfo.nearBodyPart) &&
    isNotNil(sizeInfo.distanceFromBodyPart);

  useEffect(() => {
    onHasData?.(hasData);
  }, [hasData, onHasData]);

  const handleOpenMeasureInfoInput = useCallback(() => {
    logGoodsAnalytics({
      name: 'CLICK_WRITE_MY_SIZE',
      analytics: {
        SCREEN: 'GOODS_DETAIL',
        GOODS_SNO: goodsSno,
      },
    });

    bridge
      .openGoodsMeasureInfoInputBottomSheet({
        goods_sno: goodsSno,
        full_length: selectedFullLength,
      })
      .then(result => {
        if (isNil(result)) return;

        setSelectedSizeIndex(0);
        setSizeInfo({ goodsSno, fullLength: null }, result);

        if (result.height && result.full_length) {
          logGoodsAnalytics({
            name: 'COMPLETE_WRITE_MY_SIZE',
            analytics: {
              SCREEN: 'GOODS_DETAIL',
              GOODS_SNO: goodsSno,
              HEIGHT: result.height,
              FULL_LENGTH: result.full_length,
            },
          });
        }
      });
  }, [goodsSno, selectedFullLength, logGoodsAnalytics, setSizeInfo]);

  const handleSelectSize = useCallback(() => {
    // TODO: 네이티브 셀렉터 바텀시트 연동
    // 현재는 사이즈 순환 선택으로 대체
    if (goodsPiecesFullLengthList.length <= 1) return;
    setSelectedSizeIndex(prev => (prev + 1) % goodsPiecesFullLengthList.length);
  }, [goodsPiecesFullLengthList.length]);

  if (
    isNil(sizeInfo) ||
    isNil(sizeInfo.nearBodyPart) ||
    isNil(sizeInfo.distanceFromBodyPart)
  ) {
    return null;
  }

  const sizeInfoText = getSizeInfoText({
    nearBodyPart: sizeInfo.nearBodyPart,
    distanceFromBodyPart: sizeInfo.distanceFromBodyPart,
  });

  const nudgeText = getInputNudgeText({
    isPieceFullLengthDefaultValue: sizeInfo.isPieceFullLengthDefaultValue,
    isUserHeightDefaultValue: sizeInfo.isUserHeightDefaultValue,
  });

  return (
    <Flex padding={{ horizontal: 16, top: 16 }}>
      <Flex
        backgroundColor="bg_secondary"
        borderRadius={8}
        padding={{ horizontal: 16, vertical: 16 }}
      >
        <Flex.Row gap={20}>
          <SizeVisualGuideImage
            nearBodyPart={sizeInfo.nearBodyPart}
            distanceFromBodyPart={sizeInfo.distanceFromBodyPart}
          />

          <Flex>
            <Flex flex={1} justifyContent={'center'} gap={20}>
              {goodsPiecesFullLengthList.length > 1 && selectedSize && (
                <Flex.Row
                  alignItems="center"
                  gap={4}
                  onPress={handleSelectSize}
                  backgroundColor="white"
                  borderRadius={30}
                  padding={{ horizontal: 10, vertical: 4 }}
                  alignSelf="flex-start"
                >
                  <Typography type="subtitle4" color="content_secondary">
                    {selectedSize.name}
                  </Typography>
                  <IconChevronRight
                    size={12}
                    fill={Colors.content_tertiary}
                    style={{ transform: [{ rotate: '90deg' }] }}
                  />
                </Flex.Row>
              )}

              <Flex gap={4} padding={{ left: 4 }}>
                <Typography type="h5" color="content_primary">
                  이 {withEunNeun(sizeInfo.standardCategoryName)}
                  {'\n'}
                  <Typography type="h5" color="content_blue_primary">
                    {sizeInfoText[0]}
                  </Typography>
                  {sizeInfoText[1]}
                </Typography>
                <Typography type="body3" color="content_secondary">
                  키 {sizeInfo.userHeight}cm /{' '}
                  {sizeInfo.isPieceFullLengthDefaultValue ? '평균 ' : ''}총장{' '}
                  {sizeInfo.pieceFullLength}cm
                </Typography>
              </Flex>
            </Flex>

            <Flex.Row
              alignItems="center"
              padding={{ left: 4 }}
              onPress={handleOpenMeasureInfoInput}
            >
              <Typography type="subtitle3" color="content_secondary">
                {nudgeText}
              </Typography>
              <IconChevronRight size={16} fill={Colors.content_tertiary} />
            </Flex.Row>
          </Flex>
        </Flex.Row>
      </Flex>
    </Flex>
  );
};
